import { type ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

function whenLoaded(img: HTMLImageElement): Promise<void> {
  if (img.complete) return Promise.resolve();
  return new Promise((resolve) => {
    img.addEventListener('load', () => resolve(), { once: true });
    img.addEventListener('error', () => resolve(), { once: true });
  });
}

// PrintPortal legt die Druckfassung direkt unter <body> (die übrige Seite blendet das Druck-CSS über bodyClass aus)
// und öffnet den Druckdialog, sobald Bilder und Schriften geladen sind – höchstens 4 Sekunden Wartezeit.
// „Als PDF speichern“ im Dialog ergibt eine PDF-Datei mit genau dieser Fassung.
export function PrintPortal({ bodyClass, rootClass, onDone, children }: { bodyClass: string; rootClass: string; onDone: () => void; children: ReactNode }) {
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let alive = true;
    document.body.classList.add(bodyClass);
    const images = [...(root.current?.querySelectorAll('img') ?? [])];
    const ready = Promise.all([...images.map(whenLoaded), document.fonts?.ready ?? Promise.resolve()]);
    void Promise.race([ready, new Promise((resolve) => setTimeout(resolve, 4000))]).then(() => {
      if (!alive) return;
      window.print(); // blockiert, bis der Druckdialog geschlossen ist
      document.body.classList.remove(bodyClass);
      onDone();
    });
    return () => {
      alive = false;
      document.body.classList.remove(bodyClass);
    };
  }, [bodyClass, onDone]);
  return createPortal(
    <div ref={root} className={rootClass}>
      {children}
    </div>,
    document.body,
  );
}
